import { ChangeDetectionStrategy, Component, forwardRef, Injector, Input, OnDestroy, OnInit } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BASE_URL_VALIDATORS } from "../../utils";
import { InputComponent } from "../input/input.component";
import { showCustomErrorMessageValidator } from "../validators";

@Component({
  selector: "lib-url-input",
  templateUrl: "./url-input.component.html",
  styleUrls: ["./url-input.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UrlInputComponent),
      multi: true
    }
  ]
})
export class UrlInputComponent extends InputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() override placeholder = "https://example.com";
  @Input() override type = "url";
  @Input() iconPosition: "inside" | "outside" = "outside";

  private readonly httpsPrefix = "https://";
  private readonly httpPrefix = "http://";

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private destroy$ = new Subject<void>();

  constructor(private injector: Injector) {
    super();
  }

  writeValue(value: any): void {
    if (this.control) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnInit() {
    this.initializeFormControl();

    this.control!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const correctedValue = this.normalizeUrl(value);
      if (correctedValue !== value) {
        this.control!.setValue(correctedValue, { emitEvent: false });
      }
      this.onChange(this.control!.value);
    });
  }

  onUrlKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const prefixLength = this.httpsPrefix.length;

    // Allow typing if cursor is after the prefix or prefix is missing
    if (
      !input.value.startsWith(this.httpsPrefix) ||
      (selectionStart >= prefixLength && (selectionEnd > prefixLength || !(event.key === "Delete" || event.key === "Backspace")))
    ) {
      return;
    }

    // Allow delete/backspace ONLY if the entire prefix is selected (0 to prefix.length)
    if (selectionStart === 0 && selectionEnd > prefixLength) {
      return;
    }

    // Allow navigation keys and copy/paste shortcuts
    if (
      ["ArrowLeft", "ArrowRight", "Tab", "Shift", "Control", "Meta"].includes(event.key) ||
      ((event.ctrlKey || event.metaKey) && ["c", "v", "C", "V"].includes(event.key))
    ) {
      return;
    }

    // Block all other edits in the prefix
    event.preventDefault();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormControl(): void {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl?.control) {
      this.control = ngControl.control as FormControl;
      const parentValidators = this.control.validator ? [this.control.validator] : [];
      const mergedValidators = [this.getBaseValidators(), ...parentValidators];
      this.control.setValidators(mergedValidators);
      this.control.updateValueAndValidity({ emitEvent: false });
    } else if (!this.control) {
      this.control = new FormControl(null, this.getBaseValidators());
    }
  }

  private normalizeUrl(value: any): any {
    if (!value || typeof value !== "string") {
      return "";
    }

    let cleaned = value.trim();

    if (cleaned === this.httpsPrefix || cleaned === this.httpPrefix) {
      return "";
    }

    if (cleaned.startsWith(this.httpsPrefix)) {
      return cleaned;
    }

    if (cleaned.startsWith(this.httpPrefix)) {
      return this.httpsPrefix + cleaned.substring(this.httpPrefix.length);
    }

    cleaned = cleaned.replace(new RegExp(this.httpsPrefix, "g"), "").replace(new RegExp(this.httpPrefix, "g"), "");

    return cleaned.length === 0 ? "" : this.httpsPrefix + cleaned;
  }

  private getBaseValidators() {
    return showCustomErrorMessageValidator(BASE_URL_VALIDATORS, "invalidUrl");
  }
}
